import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';

interface Review {
  id: number;
  rating: number;
  content: string;
  author_name: string;
  created_at: string;
}

interface VehicleReviewsProps {
  vehicleName: string;
}

const VehicleReviews: React.FC<VehicleReviewsProps> = ({ vehicleName }) => {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;

    const fetchReviews = async () => {
      if (!isMounted) return;

      setLoading(true);
      try {
        const response = await apiClient.get(`/reviews/vehicle/${encodeURIComponent(vehicleName)}`);
        if (isMounted) {
          setReviews(response.data.reviews);
          setAverageRating(response.data.average_rating);
          setTotalReviews(response.data.total_reviews);
        }
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (vehicleName) {
      fetchReviews();
    }

    return () => {
      isMounted = false;
    };
  }, [vehicleName]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (totalReviews === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p>{t('vehicleReviews.emptyTitle')}</p>
        <p className="text-sm mt-1">{t('vehicleReviews.emptyDesc')}</p>
      </div>
    );
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0">
        {[1, 2, 3, 4, 5].map((star) => {
          const fillPercentage = Math.min(Math.max(rating - (star - 1), 0), 1) * 100;
          const isFullStar = fillPercentage === 100;
          const isHalfStar = fillPercentage >= 50 && fillPercentage < 100;
          const isEmpty = fillPercentage === 0;

          return (
            <div key={star} className="relative w-4 h-4">
              {/* Empty star background */}
              <svg
                className="absolute w-4 h-4 text-gray-300"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth={1}
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              {/* Filled star overlay */}
              {!isEmpty && (
                <svg
                  className="absolute w-4 h-4 text-yellow-400"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth={1}
                  viewBox="0 0 24 24"
                  style={{
                    clipPath: `inset(0 ${100 - fillPercentage}% 0 0)`
                  }}
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language.startsWith('en') ? 'en-US' : 'ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const maskName = (name: string) => {
    if (name.length <= 1) return name;
    if (name.length === 2) return name[0] + '*';
    // 3글자 이상인 경우 가운데 글자들을 마스킹
    const firstChar = name[0];
    const lastChar = name[name.length - 1];
    const maskLength = name.length - 2;
    return firstChar + '*'.repeat(maskLength) + lastChar;
  };

  const REVIEWS_PER_PAGE = 3;
  const totalPages = Math.ceil(reviews.length / REVIEWS_PER_PAGE);
  const startIndex = currentPage * REVIEWS_PER_PAGE;
  const endIndex = startIndex + REVIEWS_PER_PAGE;
  const currentReviews = reviews.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      {/* 평균 별점 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <svg className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <span className="text-lg font-bold text-gray-900">{averageRating.toFixed(1)}</span>
          </div>
          <span className="text-sm text-gray-600">({t('vehicleReviews.count', { count: totalReviews })})</span>
        </div>

        {/* 페이지네이션 버튼 */}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm text-gray-600">{currentPage + 1} / {totalPages}</span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* 리뷰 목록 */}
      <div className="space-y-3">
        {currentReviews.map((review) => (
          <div key={review.id} className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {renderStars(review.rating)}
                <span className="text-sm font-medium text-gray-700">{maskName(review.author_name)}</span>
              </div>
              <span className="text-xs text-gray-500">{formatDate(review.created_at)}</span>
            </div>
            <p className="text-sm text-gray-700">{review.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VehicleReviews;
